/**
 * 会话失效（久未操作被判超时、或账号在别处登录被顶掉）的全局提示状态。
 *
 * 后端这两种情况回的都是 401，而各个页面把 401 的 message 当普通错误铺在数据卡片里，
 * 结果用户看到的是一句"请重新登录"配一个只会再失败一次的"重新加载"——整页没有一个
 * 能回到登录页的入口，只能自己去右上角点"退出"。这里把这类 401 收敛成一个全局状态，
 * 由 SessionExpiredDialog 弹窗接管，弹窗上的按钮直接把人送回登录页。
 *
 * 状态机（一次会话失效只打扰用户一次）：
 *   idle --report--> active --dismiss--> dismissed --reset(重新登录成功)--> idle
 * 弹窗弹出后背景轮询还会不断撞 401，active/dismissed 期间一律不再广播。
 */

export type SessionExpiryScope = "tenant" | "platform";
export type SessionExpiryReason = "expired" | "replaced";

export type SessionExpiryNotice = {
  scope: SessionExpiryScope;
  reason: SessionExpiryReason;
};

export type SessionExpiryState =
  | { status: "idle" }
  | { status: "active"; notice: SessionExpiryNotice }
  | { status: "dismissed" };

/**
 * 只认这几个错误码。401 并不都等于"该重新登录了"：手机上传二维码过期
 * (MOBILE_UPLOAD_TICKET_INVALID) 和登录页的密码错误 (INVALID_CREDENTIALS)
 * 也是 401，对着它们弹"重新登录"就是把公开页的访客也赶去登录。
 */
const NOTICE_BY_ERROR_CODE: Record<string, SessionExpiryNotice> = {
  AUTH_REQUIRED: { scope: "tenant", reason: "expired" },
  SESSION_REPLACED: { scope: "tenant", reason: "replaced" },
  PLATFORM_AUTH_REQUIRED: { scope: "platform", reason: "expired" },
  PLATFORM_SESSION_REPLACED: { scope: "platform", reason: "replaced" },
};

/** 从 FastAPI 信封里取错误码：error 可能是对象、字符串，也可能只在顶层 code 上。 */
export function sessionExpiryErrorCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const envelope = payload as { error?: unknown; code?: unknown };
  const error = envelope.error;
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  if (typeof envelope.code === "string" && envelope.code) return envelope.code;
  return null;
}

export function sessionExpiryNoticeFor(status: number, code: unknown): SessionExpiryNotice | null {
  if (status !== 401 || typeof code !== "string") return null;
  return NOTICE_BY_ERROR_CODE[code] ?? null;
}

let state: SessionExpiryState = { status: "idle" };
const listeners = new Set<(next: SessionExpiryState) => void>();

function publish(next: SessionExpiryState): void {
  state = next;
  for (const listener of [...listeners]) listener(state);
}

export function sessionExpiryState(): SessionExpiryState {
  return state;
}

export function activeSessionExpiryNotice(): SessionExpiryNotice | null {
  return state.status === "active" ? state.notice : null;
}

export function subscribeSessionExpiry(listener: (next: SessionExpiryState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 收到 401 响应体后调用。返回本次是否新点亮了提示，方便调用方/测试判断。
 */
export function reportSessionExpiry(status: number, payload: unknown): SessionExpiryNotice | null {
  const notice = sessionExpiryNoticeFor(status, sessionExpiryErrorCode(payload));
  if (!notice) return null;
  if (state.status !== "idle") return null;
  publish({ status: "active", notice });
  return notice;
}

/** 用户点了"重新登录"：收起弹窗，并且在重新登录成功前不再因同一次失效反复弹。 */
export function dismissSessionExpiry(): void {
  if (state.status === "dismissed") return;
  publish({ status: "dismissed" });
}

/** 重新登录成功（或需要从头开始判定）时复位。 */
export function resetSessionExpiry(): void {
  if (state.status === "idle") return;
  publish({ status: "idle" });
}

export type SessionExpiryLoginTarget = {
  path: "/login" | "/platform/login";
  redirectTo: string;
};

/**
 * 登录页的落点：租户后台回原页面，平台后台回平台后台。
 * 登录页自己的路径不能当回跳目标，否则登录成功又被弹回登录页。
 */
export function sessionExpiryLoginTarget(
  notice: SessionExpiryNotice,
  currentPath: string,
): SessionExpiryLoginTarget {
  if (notice.scope === "platform") {
    const insidePlatform = currentPath.startsWith("/platform") && currentPath !== "/platform/login";
    return { path: "/platform/login", redirectTo: insidePlatform ? currentPath : "/platform" };
  }
  const insideWorkbench = currentPath.startsWith("/admin/") || currentPath.startsWith("/m/");
  return { path: "/login", redirectTo: insideWorkbench ? currentPath : "" };
}
